/**
 * GoalFormScreen — add / edit a per-student goal (PRD §3 #2).
 *
 * Goals carry: area, goal_text, target_accuracy, current_accuracy,
 * status. v1.0-B will wire the goal-library picker; for v1.0-A we
 * use a text input for area + a textarea for goal_text.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import {
  GOAL_AREAS,
  GOAL_STATUSES,
  labelForGoalArea,
  labelForGoalStatus,
  type GoalArea,
  type GoalStatus,
} from '../lib/effectiveslp/domain';

interface Props {
  mode: 'new' | 'edit';
  studentId: string;
  goalId?: string;
  onSaved: () => void;
  onCancel: () => void;
}

interface FormState {
  area: GoalArea;
  goalText: string;
  targetAccuracy: string;
  currentAccuracy: string;
  status: GoalStatus;
}

const INITIAL: FormState = {
  area: 'articulation',
  goalText: '',
  targetAccuracy: '',
  currentAccuracy: '',
  status: 'active',
};

export function GoalFormScreen({
  mode,
  studentId,
  goalId,
  onSaved,
  onCancel,
}: Props): ReactElement {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(mode === 'new');

  useEffect(() => {
    if (mode !== 'edit' || !goalId) return;
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await supabase
        .from('goals')
        .select('area, goal_text, target_accuracy, current_accuracy, status')
        .eq('id', goalId)
        .maybeSingle();
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setLoaded(true);
        return;
      }
      if (data) {
        const row = data as {
          area: string;
          goal_text: string;
          target_accuracy: number | null;
          current_accuracy: number | null;
          status: string;
        };
        setForm({
          area: (row.area as GoalArea) ?? 'articulation',
          goalText: row.goal_text,
          targetAccuracy:
            row.target_accuracy != null ? String(row.target_accuracy) : '',
          currentAccuracy:
            row.current_accuracy != null ? String(row.current_accuracy) : '',
          status: (row.status as GoalStatus) ?? 'active',
        });
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, goalId]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const text = form.goalText.trim();
    if (!text) {
      setError('Goal text is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const tNum = form.targetAccuracy.trim() === '' ? null : parseInt(form.targetAccuracy, 10);
    const cNum = form.currentAccuracy.trim() === '' ? null : parseInt(form.currentAccuracy, 10);
    const basePayload = {
      area: form.area,
      goal_text: text,
      target_accuracy: Number.isFinite(tNum) ? tNum : null,
      current_accuracy: Number.isFinite(cNum) ? cNum : null,
      status: form.status,
    };
    try {
      if (mode === 'new') {
        const { error: iErr } = await supabase.from('goals').insert({
          ...basePayload,
          student_id: studentId,
        });
        if (iErr) {
          setError(iErr.message);
          return;
        }
      } else {
        const { error: uErr } = await supabase
          .from('goals')
          .update(basePayload)
          .eq('id', goalId ?? '');
        if (uErr) {
          setError(uErr.message);
          return;
        }
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return (
      <main className="screen">
        <p className="tagline">Loading…</p>
      </main>
    );
  }

  return (
    <main className="screen form-screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onCancel}>
          ← Cancel
        </button>
        <h1>{mode === 'new' ? 'Add goal' : 'Edit goal'}</h1>
      </header>

      <form onSubmit={(e) => void handleSubmit(e)}>
        <label>
          Area
          <select
            value={form.area}
            onChange={(e) => setField('area', e.target.value as GoalArea)}
          >
            {GOAL_AREAS.map((a) => (
              <option key={a} value={a}>
                {labelForGoalArea(a)}
              </option>
            ))}
          </select>
        </label>

        <label>
          Goal text
          <textarea
            value={form.goalText}
            onChange={(e) => setField('goalText', e.target.value)}
            placeholder="Describe the goal in clinician-friendly terms."
            required
          />
        </label>

        <label>
          Target accuracy (%)
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            value={form.targetAccuracy}
            onChange={(e) => setField('targetAccuracy', e.target.value)}
            placeholder="0–100"
          />
        </label>

        <label>
          Current accuracy (%)
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            value={form.currentAccuracy}
            onChange={(e) => setField('currentAccuracy', e.target.value)}
            placeholder="0–100"
          />
        </label>

        <label>
          Status
          <select
            value={form.status}
            onChange={(e) => setField('status', e.target.value as GoalStatus)}
          >
            {GOAL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {labelForGoalStatus(s)}
              </option>
            ))}
          </select>
        </label>

        {error && <p className="error">{error}</p>}

        <div className="form-actions">
          <button type="submit" className="snap-button" disabled={saving}>
            {saving ? 'Saving…' : mode === 'new' ? 'Add goal' : 'Save changes'}
          </button>
        </div>
      </form>
    </main>
  );
}
